import React, { useState, useEffect, useRef } from "react";
import {
  getDocDataByRef,
  getFlutterTime,
  getUserDataById,
  updateDocDataField,
} from "../models/DataFunctions";
import SecondaryButton from "../buttons/SecondaryButton";
import { useSnackbar } from "notistack";
import { useAppContext } from "../config/AppContext";
import updateDocDataFieldApi from "../api/database/updateDocDataFieldApi";
import { getUserById } from "../models/UserFunctions";

const NotesFolder = ({ userData, orgData, folder, onUpdate }) => {
  const [text, setText] = useState("");
  const [last_author, setLastAuthor] = useState(null);
  const [last_modified, setLastModified] = useState(null);
  const [items, setItems] = useState([]);
  const contentEditableRef = useRef(null);

  const { enqueueSnackbar } = useSnackbar();
  const handleClickVariant = (message, variant) => {
    enqueueSnackbar(message, { variant });
  };
  const { appState, setAppState } = useAppContext();
  const prefix = appState.dev ? appState.dev_url : appState.prod_url;

  const handleStart = async (note) => {
    if (note.last_author_id) {
      let last_author = await getUserById(note.last_author_id);
      setText(note.content);
      setLastAuthor(last_author);
      setLastModified(note.last_modified);
      const contentEditable = contentEditableRef.current;
      if (contentEditable) {
        contentEditable.innerText = "";
        await addText(note.content, last_author.email);
      }
    }

    // first clean all text
  };

  const handleSave = async () => {
    const contentEditable = contentEditableRef.current;
    const content = contentEditable.innerText;

    let notes = folder.notes ? folder.notes : [];
    // I want a forestore timestamp of now
    const filteredNotes = notes.filter((note) => note?.org_id === orgData.id);
    if (filteredNotes.length > 0) {
      let note = filteredNotes[0];
      note.content = content;
      note.last_author_id = userData.id;
      // need to have a date in seconds
      note.last_modified = getFlutterTime();
      // save note

      // remove note from folder
      notes = notes.filter((note) => note?.org_id === orgData.id);
      notes.push(note);
      notes = notes.filter((note) => note != null && note != undefined);
      // add note to folder

      await updateDocDataFieldApi(folder.ref, "notes", notes, prefix);
    } else {
      let note = {
        content: content,
        last_author_id: userData.id,
        last_modified: getFlutterTime(),
        org_id: orgData.id,
      };
      notes.push(note);
      notes = notes.filter((note) => note != null && note != undefined);
      await updateDocDataFieldApi(folder.ref, "notes", notes, prefix);
    }

    handleClickVariant("Note enregistrée", "success");
    onUpdate();
  };

  const [started, setStarted] = useState(false);

  useEffect(() => {
    if (
      folder &&
      contentEditableRef.current &&
      contentEditableRef.current.innerText === ""
    ) {
      const notes = folder.notes ? folder.notes : [];
      const filteredNotes = notes.filter((note) => note?.org_id === orgData.id);
      if (filteredNotes.length > 0 && !started) {
        setStarted(true);
        let note = filteredNotes[0];
        handleStart(note);
      }
    }

    // Example items for testing
  }, [folder, contentEditableRef]);

  function intToHexColor(intColor) {
    // Extract RGB components from the integer color
    let r = (intColor >> 16) & 0xff;
    let g = (intColor >> 8) & 0xff;
    let b = intColor & 0xff;

    // Define a threshold for "dark" colors (0 to 255)
    const brightnessThreshold = 250;

    // Calculate the brightness of the color (using an approximate formula)
    const brightness = r * 0.299 + g * 0.587 + b * 0.114;
    // If the color is too dark, lighten it by moving each component closer to 255 (white)
    if (brightness < brightnessThreshold) {
      const lightenFactor = 0.7; // How much closer to white (0.0 to 1.0, where 1.0 is full white)
      r = Math.round(r + (255 - r) * lightenFactor);
      g = Math.round(g + (255 - g) * lightenFactor);
      b = Math.round(b + (255 - b) * lightenFactor);
    }

    let res =
      "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
    if (res === "#b3b3b3") {
      return "transparent";
    }

    // Convert the RGB components back to a hex color
    return res;
  }

  const getHighlightColorForAuthor = async (author) => {
    let users = orgData.users_id;
    for (let user_id of users) {
      let user = await getUserDataById(user_id);
      if (user.email === author) {
        let colorint = user.color;
        return intToHexColor(colorint);
      }
    }
    return "transparent";
  };

  const addText = async (text, author) => {
    const contentEditable = contentEditableRef.current;
    const selection = document.getSelection();
    if (!contentEditable) return;
    // Ensure that selection is within the content-editable element
    if (selection && contentEditable.contains(selection.anchorNode)) {
      const range = selection.getRangeAt(0);

      // Create a new span with the user's color
      const span = document.createElement("span");
      let coloor = await getHighlightColorForAuthor(author);
      span.style.backgroundColor = coloor;
      span.textContent = text;

      // Insert the span at the cursor position
      range.deleteContents();
      range.insertNode(span);

      // Move the cursor to the end of the inserted span
      range.setStartAfter(span);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    } else {
      // If no selection, just append the text to the end
      const span = document.createElement("span");
      let coloor = await getHighlightColorForAuthor(author);
      span.style.backgroundColor = coloor;
      span.textContent = text;
      contentEditable.appendChild(span);
    }
  };

  const handleBeforeInput = (event) => {
    event.preventDefault(); // Prevent default text insertion
    addText(event.data, userData.email); // Add custom formatted text
  };

  return (
    <div className="flex flex-col gap-2">
      <div
        contentEditable
        ref={contentEditableRef}
        onBeforeInput={handleBeforeInput}
        dangerouslySetInnerHTML={{ __html: "" }}
        style={{
          width: "100%",
          border: "1px solid #ccc",
          borderRadius: "10px",
          padding: "10px",
          fontSize: "14px",
          minHeight: "100px",
          whiteSpace: "pre-wrap",
        }}
      />

      {last_author && (
        <span className=" text-shadow_50 text-h3">
          Dernière modification par{" "}
          <span
            className="rounded-xl px-1 h-fit"
            style={{ backgroundColor: intToHexColor(last_author.color) }}
          >
            {last_author.display_name}
          </span>{" "}
          <span>
            le {new Date(last_modified.seconds * 1000).toLocaleString()}
          </span>
        </span>
      )}

      <div className="flex flex-row justify-end">
        {" "}
        <SecondaryButton onClick={handleSave} className="w-fit">
          Enregistrer
        </SecondaryButton>
      </div>
    </div>
  );
};

export default NotesFolder;
